import React, { useMemo, useState } from 'react';

import { PlusOutlined, UploadOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { Tag, Button, Tooltip, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Can from '@shared/permissions/Can';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext';
import FullDayRangePicker from '@components/shared/FullDayRangePicker';
import { useImport } from '@hooks/shared/useImport';
import ImportModal from '@shared/ImportModal';
import ListPageHeader from '@shared/ListPageHeader';
import StockAdjustmentConceptModal from '@shared/stock_adjustments/StockAdjustmentConceptModal';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { MultipleValue } from '@shared/table/Rules/component';
import { downloadImportTemplate } from '@utils/downloadTemplate';

import { stockAdjustmentService } from '@services/operator/stockAdjustmentService';

interface StockAdjustmentRow {
  id: string;
  name?: string;
  status?: string;
  note?: string;
  applicant_name?: string;
  created_at?: string;
  [key: string]: unknown;
}

function StockAdjustmentsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const operatorData = useOperatorData() as { merchants?: Array<{ id: string; name: string }> };
  const merchants = operatorData?.merchants || [];
  const { addJob } = useOperatorNotification();
  const [lastModified, setLastModified] = useState(0);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);

  // useImport 來自 .js，TS 從 JSDoc 推得不正確簽名，cast 為 any 保留原行為
  const handleImport = (useImport as unknown as (opts: Record<string, unknown>) => (...args: unknown[]) => Promise<unknown>)({
    action: (formData: FormData) => stockAdjustmentService.import(formData),
    addJob,
    jobName: 'shared.stock_adjustment.import.job_name',
  });

  const handleDownloadTemplate = async (merchantId: string) => {
    await downloadImportTemplate({
      service: stockAdjustmentService,
      language: i18n.language,
      merchantId,
      t,
      successKey: 'shared.stock_adjustment.import.template_success',
      errorKey: 'shared.stock_adjustment.import.template_error'
    });
  };

  const columns: unknown[] = [
    {
      name: t('models.stock_adjustment.name'),
      selector: (row: StockAdjustmentRow) => row.name,
      sortable: true,
      sortFieldName: 'stock_adjustments.name',
      cell: (row: StockAdjustmentRow) => <Link to={`/stock_adjustments/${row.id}`}>{row.name}</Link>
    },
    {
      name: t('models.stock_adjustment.status'),
      selector: (row: StockAdjustmentRow) => row.status,
      cell: (row: StockAdjustmentRow) => {
        let color = 'default';
        if (row.status === 'approved') color = 'success';
        if (row.status === 'rejected') color = 'error';
        if (row.status === 'pending') color = 'processing';
        return <Tag color={color}>{t(`enums.stock_adjustment.status.${row.status}`)}</Tag>;
      }
    },
    {
      name: t('models.stock_adjustment.note'),
      selector: (row: StockAdjustmentRow) => row.note || '-',
    },
    {
      name: t('models.stock_adjustment.applicant'),
      selector: (row: StockAdjustmentRow) => row.applicant_name,
    },
    {
      name: t('models.stock_adjustment.created_at'),
      selector: (row: StockAdjustmentRow) => row.created_at,
      sortable: true,
      sortFieldName: 'stock_adjustments.created_at',
    }
  ];

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('models.stock_adjustment.name'),
      component: MultipleValue,
      inputName: 'stock_adjustments.name',
      defaultPinned: true,
      createable: true,
    },
    {
      label: t('models.stock_adjustment.status'),
      component: MultipleValue,
      inputName: 'stock_adjustments.status',
      createable: false,
      defaultOptions: [
        { label: t('enums.stock_adjustment.status.draft'), value: 'draft' },
        { label: t('enums.stock_adjustment.status.pending'), value: 'pending' },
        { label: t('enums.stock_adjustment.status.approved'), value: 'approved' },
        { label: t('enums.stock_adjustment.status.rejected'), value: 'rejected' },
      ],
    },
    {
      label: t('models.stock_adjustment.created_at'),
      component: FullDayRangePicker,
      inputName: 'stock_adjustments.created_at',
    }
  ], [t]);

  return (
    <div>
      <ListPageHeader
        items={['stocks', 'stocks.adjustments']}
        guideButton={
          <Tooltip title={t('concepts.stock_adjustments.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Space>
            <Can requiredPermission="inventories:write">
              <Button
                icon={<UploadOutlined />}
                onClick={() => setImportModalVisible(true)}
              >
                {t('common.button.import')}
              </Button>
            </Can>
            <Can requiredPermission="inventories:write">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/stock_adjustments/new')}
              >
                {t('common.button.new')}
              </Button>
            </Can>
          </Space>
        }
      />
      <TableIndex
        columns={columns}
        tableId="StockAdjustmentsTable"
        service={stockAdjustmentService as unknown as React.ComponentProps<typeof TableIndex>['service']}
        defaultQuery={{
          sorting_field: 'stock_adjustments.created_at',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.stock_adjustment.name'),
            t('models.stock_adjustment.note'),
          ],
        }}
        lastModified={lastModified}
        setLastModified={setLastModified}
      />
      <ImportModal
        open={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        // onImport / onDownloadTemplate 簽名來自 ImportModal.tsx，runtime 兼容但宣告嚴格；以 any cast 保留行為
        onImport={handleImport as unknown as (file: unknown, merchantId: string | null, overrideExisting: boolean, importType?: string | null, attachments?: unknown[], note?: string) => Promise<unknown>}
        onDownloadTemplate={handleDownloadTemplate as unknown as (merchantId?: string) => unknown}
        role="operator"
        merchants={merchants}
        title={t('shared.stock_adjustment.import.modal_title')}
        showOverrideExisting={false}
      />
      <StockAdjustmentConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  );
}

export default StockAdjustmentsList;
